import React, { useState } from "react";
import { Badge, Button, Card, Dialog, DropdownMenu, Flex, IconButton, Text } from "@radix-ui/themes";
import { EllipsisHorizontalIcon } from "@heroicons/react/16/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { disconnectHubRise } from "../../../api/QrOrderingApi";
import { HubRiseIcon } from "./HubRiseIcon";

export function ActiveHubRiseIntegration({ connections }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const disconnect = async () => {
    setPending(true);
    setError("");
    try {
      await disconnectHubRise(target.location_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["hubrise-connections"] }),
        queryClient.invalidateQueries({ queryKey: ["hubrise-connection"] }),
      ]);
      setTarget(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not disconnect HubRise. Please try again.");
    } finally {
      setPending(false);
    }
  };
  return (
    <Flex direction="column" gap="3">
      {connections.filter((connection) => connection.is_active).map((connection) => (
        <Card key={connection.location_id}>
          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Flex gap="3" align="center">
              <HubRiseIcon />
              <Flex direction="column" gap="1">
              <Text size="4" weight="medium">HubRise</Text>
              <Text size="2">B-Platform location: {connection.location_name}</Text>
              <Text size="2" color="gray">HubRise location: {connection.hubrise_location_name || connection.hubrise_location_id}</Text>
              <Text size="2" color="gray">Catalog: {connection.catalog_name || connection.catalog_id || "Not selected"}</Text>
              </Flex>
            </Flex>
            <Flex gap="3" align="center">
              <Badge color="green">Connected</Badge>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <IconButton size="1" variant="ghost" color="gray" aria-label="HubRise actions">
                    <EllipsisHorizontalIcon width="16" height="16" />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onSelect={() => navigate("/integrations/browse-all")}>Manage</DropdownMenu.Item>
                  <DropdownMenu.Item color="red" onSelect={() => { setTarget(connection); setError(""); }}>Disconnect</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Flex>
          </Flex>
        </Card>
      ))}
      <Dialog.Root open={Boolean(target)} onOpenChange={(open) => { if (!open && !pending) setTarget(null); }}>
        <Dialog.Content maxWidth="450px" onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }}>
          <Dialog.Title>Disconnect HubRise</Dialog.Title>
          <Dialog.Description>Disconnect HubRise for {target?.location_name}? Menu sync and order delivery through this connection will stop. You can reconnect later.</Dialog.Description>
          {error && <Text as="p" role="alert" color="red" mt="3">{error}</Text>}
          <Flex gap="3" justify="end" mt="4">
            <Button variant="soft" color="gray" disabled={pending} onClick={() => setTarget(null)}>Cancel</Button>
            <Button color="red" disabled={pending} loading={pending} onClick={disconnect}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
  );
}
